import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Spinner,
  PasswordInput,
  BrandLockup,
} from "@opero/ui";
import { SsoButtons } from "@opero/ui/auth";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import { polaris } from "../lib/polaris-client";
import { passwordSchema } from "../lib/password";
import { useSsoProviders } from "../hooks/use-sso-providers";
import { AuthModalShell } from "./AuthModalShell";
import { ResendVerification } from "./ResendVerification";

const signupSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Enter a valid email address"),
  password: passwordSchema,
});

type SignupFormValues = z.infer<typeof signupSchema>;

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

export function SignupModal({
  isOpen,
  onClose,
  onSwitchToLogin,
}: SignupModalProps) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const {
    providers: ssoProviders,
    redirecting: ssoRedirecting,
    select: handleSsoSelect,
  } = useSsoProviders({
    isOpen,
    mode: "signup",
    onError: useCallback((message: string | null) => setApiError(message), []),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: SignupFormValues) => {
    setApiError(null);
    setIsLoading(true);
    try {
      await polaris.auth.registerAccount({
        // Generated request-body type is mis-referenced upstream; cast to the
        // SDK's declared body type.
        body: {
          email: data.email,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
          redirectUrl: window.location.origin,
        } as unknown as Parameters<
          typeof polaris.auth.registerAccount
        >[0]["body"],
      });
      setSubmittedEmail(data.email);
      setSuccess(true);
    } catch (err: unknown) {
      setApiError(getErrorMessage(err, "Registration failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setApiError(null);
    setSubmittedEmail("");
    reset();
    onClose();
  };

  return (
    <AuthModalShell
      isOpen={isOpen}
      onClose={handleClose}
      brand={<BrandLockup />}
    >
      <div className="px-7 pb-7 pt-2">
        {success ? (
          <div className="space-y-4 text-center py-4">
            <div className="rounded-lg border border-accent-500/20 bg-accent-500/5 p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Check your email
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                We&apos;ve sent a verification link to{" "}
                <strong>{submittedEmail}</strong>.
              </p>
            </div>
            <ResendVerification email={submittedEmail} />
            <button
              onClick={() => {
                handleClose();
                onSwitchToLogin();
              }}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Back to login
            </button>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Create your account
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Get started with Opero Task Management
              </p>
            </div>
            {ssoProviders.length > 0 && (
              <div className="mb-5">
                <SsoButtons
                  providers={ssoProviders}
                  onSelect={handleSsoSelect}
                  isLoading={ssoRedirecting || isLoading}
                  separatorLabel="Or sign up with email"
                />
              </div>
            )}
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="space-y-4"
            >
              {apiError && (
                <Alert variant="destructive">
                  <AlertDescription className="text-sm text-danger">
                    {apiError}
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>First name</Label>
                  <Input {...register("firstName")} placeholder="Jane" />
                  {errors.firstName && (
                    <p className="text-xs text-red-500">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Last name</Label>
                  <Input {...register("lastName")} placeholder="Doe" />
                  {errors.lastName && (
                    <p className="text-xs text-red-500">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  {...register("email")}
                  placeholder="you@example.com"
                />
                {errors.email && (
                  <p className="text-xs text-red-500">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <PasswordInput
                  {...register("password")}
                  placeholder="Create a password"
                />
                {errors.password && (
                  <p className="text-xs text-red-500">
                    {errors.password.message}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner size="sm" /> Creating account...
                  </>
                ) : (
                  "Create account"
                )}
              </Button>
              <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    onSwitchToLogin();
                  }}
                  className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  Sign in
                </button>
              </p>
            </form>
          </>
        )}
      </div>
    </AuthModalShell>
  );
}
